/* eslint-disable react-hooks/exhaustive-deps */
import React from "react";
import { adsAPI, compressorAPI } from "../..";
import { RTA } from "../../services/negotiationBridge";
import localforage from "localforage";
import { sessionInfo } from "../../utils/utils";
import { MenuItem } from "react-pro-sidebar";
import CloudUploadTwoToneIcon from "@mui/icons-material/CloudUploadTwoTone";
import {
  CheckCircleOutlineRounded,
  CloseRounded,
  HighlightOffTwoTone,
  ScheduleRounded,
} from "@mui/icons-material";
import { useAnimatedNumber } from "../AnimateNumber";
import { ProgressBar } from "../ProgressBar";
import { LinearProgress } from "@mui/material";
import { Chip } from "../Chip";
import { checkPermission, usePermissions } from "../PermissionsContext";
import {
  UPLOAD_KIND_PERMISSION,
  UPLOAD_SECTION_TO_KIND,
  uploadKindChip,
  uploadKindDefaultName,
  type UploadKind,
} from "./uploadKinds";
import "./UploadToast.css";

interface UploadBarProps {
  onClick: () => void;
}

/**
 * The sidebar row that opens the upload toast.
 *
 * It is a `react-pro-sidebar` `<MenuItem>`, and that library requires a `<Menu>`
 * ancestor: `useMenu()` reads a context created with `createContext(undefined)`
 * and throws "Menu Component is required!" without one. In the app the ancestor
 * is the `<Sidebar><Menu>` in src/routes/Root.tsx; in isolation it has to be
 * asked for, which is what the line below does.
 *
 * @story provider menu
 */
export const UploadOptionBar: React.FC<UploadBarProps> = (props) => {
  return (
    <MenuItem
      icon={<CloudUploadTwoToneIcon />}
      className="uploadToastMenuItem"
      onClick={props.onClick}
    >
      Uploads
    </MenuItem>
  );
};

interface UploadToastProps extends React.HTMLProps<HTMLDivElement> {
  isOpen?: boolean;
  onClose?: () => void;
  onNewUpload?: () => void;
}

// The kind vocabulary — section→kind, chip text, default name and the gating
// permission — lives in ./uploadKinds.ts, where it is unit-tested as one table.
type feature = UploadKind;

// An item is "queued" ("Na fila") between the 202 accept (the compressor took
// the job into its durable queue) and the first progress event (a worker picked
// it up and started transcoding). Any progress update flips it back to the
// default active/processing rendering. `notice` carries the friendly message of
// the rare backlog-cap soft reject, which is shown as a queued item, never as an
// error.
type UploadItemStatus = "queued" | "finalizing";

interface UploadToastItems {
  id: string;
  name: string;
  feature: feature;
  progressNumber: number;
  pendingCancel?: boolean;
  groupId?: string;
  memberIds?: string[];
  status?: UploadItemStatus;
  notice?: string;
  // When the item was first seen and last touched (progress/upsert). Used to
  // self-heal items whose conclude event was dropped or mis-keyed: a periodic
  // sweep removes items that have gone quiet for too long.
  startedAt?: number;
  updatedAt?: number;
}

// Reconcile cadence and self-heal thresholds for the upload toast. These keep
// the "Uploads em Andamento" list honest under concurrent, multi-collaborator
// uploads even when a conclude event never arrives with a usable id.
const RECONCILE_INTERVAL_MS = 20000;
// An ad-kind item absent from GET /compressions for longer than this grace
// window is treated as a ghost (its conclude was dropped) and pruned.
const GHOST_GRACE_MS = 15000;
// Hard ceiling: any item quiet for this long is removed regardless of kind, so
// a stuck or mis-keyed upload cannot linger on the list forever.
const ITEM_MAX_AGE_MS = 4 * 60 * 1000;
// The two frontend-only avatar kinds are NOT reported by GET /compressions
// (that endpoint covers ads: radio/tv/hall). They must never be ghost-pruned
// by the ads reconcile; only the hard max-age sweep may remove them.
const AD_FEATURES: ReadonlySet<feature> = new Set(["radio", "tv", "hall"]);
// The compressor's variant/original S3 upload phase emits no progress tick, so a
// heavy upload's determinate bar sits frozen near 100% for the whole push. Once a
// near-complete item has gone this long without a progress update, swap the stuck
// bar for an indeterminate "Finalizando envio..." affordance so the row reads as
// "still working" rather than hung. We never invent a fake percentage.
const PROGRESS_PLATEAU_MS = 8000;
// Only near-complete items get the finishing affordance; a stall lower down the
// bar stays determinate (an honest "stuck here") and is left to the max-age sweep.
const PROGRESS_PLATEAU_MIN_PERCENT = 90;
// How many concluded ids stay guarded against RTA redelivery. Evicted FIFO (oldest
// first) once exceeded, so recent concludes keep protecting just-finished items
// from being resurrected at 0% by a late receiveNewUploadItem.
const CONCLUDED_IDS_CAP = 500;

interface UploadToastItemProps {
  item: UploadToastItems;
  isLast: boolean;
  onCancelClick?: (id: string | string[]) => void;
}

const UploadToastItem = React.memo(
  ({ item, isLast, onCancelClick }: UploadToastItemProps) => {
    const animatedProgress = useAnimatedNumber(item.progressNumber, 600);

    // A near-complete, still-processing item whose progress has not advanced for
    // PROGRESS_PLATEAU_MS is almost certainly in the compressor's tick-less S3
    // upload phase. Flip it to the indeterminate finishing affordance so a frozen
    // bar reads as "still working". Any progress update (updatedAt/progressNumber
    // change) re-runs this effect and cancels the pending flip; queued and
    // cancelling rows are never treated as plateaued.
    const [finalizing, setFinalizing] = React.useState(false);
    React.useEffect(() => {
      setFinalizing(false);
      const processing = !item.pendingCancel && item.status !== "queued";
      if (!processing || item.progressNumber < PROGRESS_PLATEAU_MIN_PERCENT) return;
      const timer = window.setTimeout(() => setFinalizing(true), PROGRESS_PLATEAU_MS);
      return () => window.clearTimeout(timer);
    }, [item.updatedAt, item.progressNumber, item.status, item.pendingCancel]);

    return (
      <>
        <div className="uploadToastItem">
          <div className="uploadToastItemInfos">
            <div className="uploadToastItemOnlyInfos">
              <span className="uploadToastItemName">{item.name}</span>
              {item.pendingCancel ? (
                <Chip class="uploadToastItemFeatureChipCancel">
                  <span>{uploadKindChip(item.feature)} - Cancelando...</span>
                </Chip>
              ) : item.status === "queued" ? (
                <Chip class="uploadToastItemFeatureChip">
                  <span>{uploadKindChip(item.feature)} - Na fila</span>
                </Chip>
              ) : item.status === "finalizing" || finalizing ? (
                <Chip class="uploadToastItemFeatureChip">
                  <span>{uploadKindChip(item.feature)} - Finalizando...</span>
                </Chip>
              ) : (
                <Chip class="uploadToastItemFeatureChip">
                  <span>{uploadKindChip(item.feature)}</span>
                </Chip>
              )}
            </div>

            {!item.pendingCancel && item.status !== "queued" && item.progressNumber < 100 && (
              <button
                className="uploadToastItemCancelButton"
                disabled={item.pendingCancel}
                onClick={() => onCancelClick?.(item.memberIds ?? item.id)}
              >
                <HighlightOffTwoTone />
              </button>
            )}
          </div>

          <div className="uploadToastItemProgress">
            {item.pendingCancel ? (
              <LinearProgress className="uploadToastItemProgressBar" color="inherit" />
            ) : item.status === "queued" ? (
              // Queued ("Na fila"): waiting in the compressor's durable queue for
              // a worker. A clock affordance, deliberately not the progress bar
              // and not an error, so a queued item reads as "aguarde", not "falhou".
              <div className="uploadToastItemQueued">
                <ScheduleRounded className="uploadToastItemQueuedIcon" />
                <span>{item.notice ?? "Na fila, aguarde..."}</span>
              </div>
            ) : item.status === "finalizing" || finalizing ? (
              // Plateaued near completion: the S3 upload phase emits no ticks, so
              // reuse the same indeterminate bar as the cancelling state instead
              // of a determinate bar frozen at its last percentage. The
              // "Finalizando..." label rides on the chip, mirroring "Cancelando...".
              <LinearProgress className="uploadToastItemProgressBar" color="inherit" />
            ) : (
              <>
                <ProgressBar className="uploadToastItemProgressBar" value={animatedProgress} />
                <span>{animatedProgress}%</span>
              </>
            )}
          </div>
        </div>
        {!isLast && <hr className="uploadToastItemSeparator" />}
      </>
    );
  },
  (prev, next) =>
    prev.item.progressNumber === next.item.progressNumber &&
    prev.item.pendingCancel === next.item.pendingCancel &&
    prev.item.status === next.item.status &&
    prev.item.notice === next.item.notice &&
    // The real creative name arrives after the default name via a fresh upsert
    // with progressNumber unchanged; without comparing it the row would stay
    // stuck on the generic default ("Anuncio de radio"/"Anuncio do Atrio").
    prev.item.name === next.item.name &&
    // id and memberIds are the cancel target (memberIds is a new array each
    // aggregation, so compare by value); a stale target must never survive here.
    prev.item.id === next.item.id &&
    (prev.item.memberIds?.join(",") ?? "") === (next.item.memberIds?.join(",") ?? "") &&
    prev.isLast === next.isLast,
);

type UploadToastOpener = () => void;
let uploadToastOpener: UploadToastOpener | null = null;

export function registerUploadToastOpener(opener: UploadToastOpener | null) {
  uploadToastOpener = opener;
}

export function triggerUploadToastOpen() {
  uploadToastOpener?.();
}

// Local progress publisher: chunk uploads run client-side and finish before
// the compressor starts emitting RTA progress events, so we surface chunk
// completions through the same SocketEvents pipeline the RTA path uses. The
// UploadToast component registers itself once mounted; pages call this from
// createTurboUpload's onProgress.
type LocalUploadProgressFn = (id: string, progress: number, section: string) => void;
let localUploadProgressEmitter: LocalUploadProgressFn | null = null;

export function registerLocalUploadProgressEmitter(fn: LocalUploadProgressFn | null) {
  localUploadProgressEmitter = fn;
}

export function publishLocalUploadProgress(id: string, progress: number, section: string) {
  localUploadProgressEmitter?.(id, progress, section);
}

// Friendly fallback for the rare per-user backlog-cap soft reject. The compressor
// sends its own `message`, but keep a natural pt-BR default so the toast never
// falls back to a scary or empty string.
export const UPLOAD_BACKLOG_NOTICE =
  "Muitos uploads na fila, aguarde os uploads atuais terminarem.";

// Queued publisher: the durable-queue compressor returns 202 the moment a job is
// accepted into the queue (before any worker picks it up), and 429 + body
// `{error:"backlog_full", ...}` on the rare anti-abuse backlog cap. Both mean
// "na fila", not a failure, so createTurboUpload routes them here (keyed by the
// same mediaId the progress/conclude events use). A `notice` marks the backlog
// soft reject, which self-clears since no progress or conclude event follows it.
type UploadQueuedFn = (id: string, section: string, notice?: string) => void;
let uploadQueuedEmitter: UploadQueuedFn | null = null;

export function registerUploadQueuedEmitter(fn: UploadQueuedFn | null) {
  uploadQueuedEmitter = fn;
}

export function publishUploadQueued(id: string, section: string, notice?: string) {
  uploadQueuedEmitter?.(id, section, notice);
}

export const AD_UPLOAD_SETTLED_EVENT = "dakasa:enterprise-ad-upload-settled";

function publishAdUploadSettled(feature: feature, targetId?: string) {
  window.dispatchEvent(new CustomEvent(AD_UPLOAD_SETTLED_EVENT, { detail: { feature, targetId } }));
}

const NoUploadsInProgress: React.FC = () => {
  return (
    <div id="noUploadsInProgressContainer">
      <CheckCircleOutlineRounded className="noUploadsInProgressIcon" />
      <span>Não há uploads em andamento.</span>
    </div>
  );
};

export const UploadToast: React.FC<UploadToastProps> = (props) => {
  const [items, setItems] = React.useState<UploadToastItems[]>([]);

  const { permissions, permissionsLoaded } = usePermissions();
  const uploadMeta = React.useRef<
    Record<string, { name: string; feature: feature; groupId?: string; hallAdId?: string }>
  >({});
  const sectionToFeatureMap: Record<string, feature> = UPLOAD_SECTION_TO_KIND;
  const prevItemsLengthRef = React.useRef(items.length);
  const onNewUploadRef = React.useRef<(() => void) | undefined>(props.onNewUpload);
  const permissionsRef = React.useRef(permissions);
  const permissionsLoadedRef = React.useRef(permissionsLoaded);
  const pendingUploadsRef = React.useRef<UploadToastItems[]>([]);
  const itemsMapRef = React.useRef<Map<string, UploadToastItems>>(new Map());
  const concludedIdsRef = React.useRef<Set<string>>(new Set());
  const serverPendingTargetsRef = React.useRef<Map<string, feature>>(new Map());

  const normalizeUploadId = (value: string | number | undefined | null) => {
    if (value === undefined || value === null) return "";
    return `${value}`;
  };

  const normalizeNameForKey = (value: string) => value.trim().toLowerCase();
  const buildFallbackKey = (name: string, feature: feature) => {
    return `fallback:${feature}:${normalizeNameForKey(name)}`;
  };

  const buildNameFeatureKey = (name: string, feature: feature) => {
    return `${feature}:${normalizeNameForKey(name)}`;
  };

  const buildHallGroupId = (value: string) => `hall:${normalizeNameForKey(value)}`;
  const buildHallGroupIdFrom = (hallAdId?: string, name?: string, fallbackGroupId?: string) => {
    const normalizedHallAd = normalizeUploadId(hallAdId);
    if (normalizedHallAd) return buildHallGroupId(normalizedHallAd);
    if (fallbackGroupId) return fallbackGroupId;
    if (name) return buildHallGroupId(name);
    return undefined;
  };

  const isFallbackKey = (key: string) => key.startsWith("fallback:");

  const choosePreferredEntryKey = (
    existingKey: string,
    newKey: string,
    existingItem: UploadToastItems,
    incomingItem: UploadToastItems,
  ) => {
    const existingIsFallback = isFallbackKey(existingKey);
    const newIsFallback = isFallbackKey(newKey);

    if (existingIsFallback !== newIsFallback) {
      return existingIsFallback ? newKey : existingKey;
    }

    if (incomingItem.progressNumber !== existingItem.progressNumber) {
      return incomingItem.progressNumber > existingItem.progressNumber ? newKey : existingKey;
    }

    if (incomingItem.pendingCancel && !existingItem.pendingCancel) {
      return newKey;
    }

    return existingKey;
  };

  const deduplicateItemsMap = () => {
    if (itemsMapRef.current.size <= 1) return;

    const normalizedKeyToMapKey = new Map<string, string>();
    const dedupedMap = new Map<string, UploadToastItems>();

    for (const [key, entry] of itemsMapRef.current.entries()) {
      if (entry.feature === "hall") {
        dedupedMap.set(key, { ...entry, id: key });
        continue;
      }
      const normalizedKey = buildNameFeatureKey(entry.name, entry.feature);
      const existingKey = normalizedKeyToMapKey.get(normalizedKey);

      if (!existingKey) {
        normalizedKeyToMapKey.set(normalizedKey, key);
        dedupedMap.set(key, { ...entry, id: key });
        continue;
      }

      const existingEntry = dedupedMap.get(existingKey);
      if (!existingEntry) {
        normalizedKeyToMapKey.set(normalizedKey, key);
        dedupedMap.set(key, { ...entry, id: key });
        continue;
      }

      const preferredKey = choosePreferredEntryKey(existingKey, key, existingEntry, entry);
      const preferredEntry = preferredKey === existingKey ? existingEntry : entry;
      const mergedEntry: UploadToastItems = {
        ...preferredEntry,
        progressNumber: Math.max(existingEntry.progressNumber, entry.progressNumber),
        pendingCancel: existingEntry.pendingCancel || entry.pendingCancel,
        id: preferredKey,
      };

      dedupedMap.delete(existingKey);
      dedupedMap.delete(key);
      dedupedMap.set(preferredKey, mergedEntry);
      normalizedKeyToMapKey.set(normalizedKey, preferredKey);
    }

    itemsMapRef.current = dedupedMap;
  };

  const syncItemsFromMap = () => {
    deduplicateItemsMap();
    setItems(Array.from(itemsMapRef.current.values()));
  };

  const findKeyByNameFeature = (name: string, feature: feature) => {
    const targetName = normalizeNameForKey(name);
    for (const [key, item] of itemsMapRef.current.entries()) {
      if (item.feature === feature && normalizeNameForKey(item.name) === targetName) {
        return key;
      }
    }
    return undefined;
  };

  const upsertUploadItem = (mapKey: string, item: UploadToastItems) => {
    const existing = itemsMapRef.current.get(mapKey);
    const pendingCancel = existing?.pendingCancel ?? item.pendingCancel ?? false;
    // A progress update is the worker transcoding: it flips a queued item to the
    // active/processing rendering. When leaving the queued state let the incoming
    // (transcode) progress take over even if it is lower than the 100% the client
    // upload reached, so the bar restarts from the real transcode value.
    const leavingQueued = existing?.status === "queued";
    // Late "new item" events (e.g. receiveNewUploadItem fired by the server
    // after the client-side chunk loop already advanced the bar) carry
    // progressNumber=0. Don't regress the visible progress — keep the higher
    // of the two so the toast doesn't flicker back to 0%.
    const progressNumber =
      existing && !leavingQueued && item.progressNumber < existing.progressNumber
        ? existing.progressNumber
        : item.progressNumber;
    const now = Date.now();
    itemsMapRef.current.set(mapKey, {
      ...item,
      id: mapKey,
      progressNumber,
      pendingCancel,
      groupId: item.groupId ?? existing?.groupId,
      // A progress update always means "processing": clear any queued marker.
      status: undefined,
      notice: undefined,
      startedAt: existing?.startedAt ?? now,
      updatedAt: now,
    });
    syncItemsFromMap();
  };

  // Mark an item as queued ("Na fila") without disturbing its progress. Used on
  // the 202 accept and on the backlog-cap soft reject. Preserves an existing
  // item (so a mid-upload item just gains the queued marker) or creates one at
  // 100% (the client-side upload has finished by the time either signal fires).
  const upsertQueuedItem = (
    mapKey: string,
    item: { name: string; feature: feature; groupId?: string; notice?: string },
  ) => {
    const existing = itemsMapRef.current.get(mapKey);
    const now = Date.now();
    itemsMapRef.current.set(mapKey, {
      id: mapKey,
      name: existing?.name ?? item.name,
      feature: existing?.feature ?? item.feature,
      progressNumber: existing?.progressNumber ?? 100,
      pendingCancel: existing?.pendingCancel ?? false,
      groupId: item.groupId ?? existing?.groupId,
      memberIds: existing?.memberIds,
      status: "queued",
      notice: item.notice,
      startedAt: existing?.startedAt ?? now,
      updatedAt: now,
    });
    syncItemsFromMap();
  };

  // Remove any items whose feature matches `feature` (and name, when known).
  // The self-heal path for a conclude event that arrives without a usable id:
  // for the two singleton avatar kinds a feature match is unambiguous; for the
  // ad kinds we only match when a name is also supplied so we never wipe an
  // unrelated concurrent upload of the same kind.
  const removeItemsByFeature = (feature: feature, name?: string) => {
    const normalizedName = name ? normalizeNameForKey(name) : undefined;
    const isSingletonAvatar = feature === "orgAvatar" || feature === "collaboratorAvatar";
    let mutated = false;
    for (const [key, entry] of Array.from(itemsMapRef.current.entries())) {
      if (entry.feature !== feature) continue;
      const nameMatches = !normalizedName || normalizeNameForKey(entry.name) === normalizedName;
      if (!isSingletonAvatar && !nameMatches) continue;
      itemsMapRef.current.delete(key);
      mutated = true;
    }
    if (mutated) syncItemsFromMap();
  };

  const ensureUniqueItemKey = (
    desiredKey: string,
    name: string,
    feature: feature,
    groupId?: string,
  ) => {
    if (feature === "hall" && groupId) {
      const normalizedName = normalizeNameForKey(name);
      let carryPending = false;
      itemsMapRef.current.forEach((entry, key) => {
        if (entry.feature !== "hall") return;
        const sameGroup =
          entry.groupId === groupId ||
          buildHallGroupIdFrom(uploadMeta.current[key]?.hallAdId) === groupId ||
          normalizeNameForKey(entry.name) === normalizedName;
        if (!sameGroup || key === desiredKey) return;
        carryPending = carryPending || entry.pendingCancel === true;
        itemsMapRef.current.delete(key);
      });
      const existing = itemsMapRef.current.get(desiredKey);
      if (existing) {
        carryPending = carryPending || existing.pendingCancel === true;
      }
      return carryPending;
    }
    const existingKey = findKeyByNameFeature(name, feature);
    if (existingKey && existingKey !== desiredKey) {
      const existingItem = itemsMapRef.current.get(existingKey);
      if (existingItem) {
        itemsMapRef.current.delete(existingKey);
        return existingItem.pendingCancel ?? false;
      }
    }
    const existing = itemsMapRef.current.get(desiredKey);
    return existing?.pendingCancel ?? false;
  };

  const removeUploadItem = (id: string, fallback?: { name: string; feature: feature }) => {
    if (itemsMapRef.current.delete(id)) {
      syncItemsFromMap();
      return;
    }
    if (fallback) {
      const fallbackKey = findKeyByNameFeature(fallback.name, fallback.feature);
      if (fallbackKey) {
        itemsMapRef.current.delete(fallbackKey);
        syncItemsFromMap();
      }
    }
  };

  const updatePendingCancel = (id: string | string[], pending: boolean) => {
    const targets = Array.isArray(id) ? id : [id];
    const normalizedTargets = targets.map(normalizeUploadId).filter(Boolean);
    if (normalizedTargets.length === 0) return;

    let mutated = false;
    const hallNamesByGroup = new Map<string, string>();
    const hallAdIdsByGroup = new Map<string, string>();

    normalizedTargets.forEach((targetId) => {
      const existing = itemsMapRef.current.get(targetId);
      if (!existing) return;
      mutated = true;
      itemsMapRef.current.set(targetId, {
        ...existing,
        pendingCancel: pending,
      });
      if (existing.feature === "hall") {
        const groupKey = existing.groupId ?? buildHallGroupId(existing.name);
        hallNamesByGroup.set(groupKey, normalizeNameForKey(existing.name));
        const metaHall = uploadMeta.current[targetId]?.hallAdId;
        if (metaHall) {
          const metaGroup = buildHallGroupIdFrom(metaHall);
          if (metaGroup) {
            hallAdIdsByGroup.set(groupKey, metaGroup);
          }
        }
      }
    });

    // Also mark any hall items that belong to the same group/name as pending.
    if (hallNamesByGroup.size > 0) {
      itemsMapRef.current.forEach((entry, key) => {
        if (entry.feature !== "hall") return;
        const groupKey = entry.groupId ?? buildHallGroupId(entry.name);
        const normalizedName = normalizeNameForKey(entry.name);
        const matchesGroup =
          hallNamesByGroup.has(groupKey) ||
          Array.from(hallNamesByGroup.values()).some((name) => name === normalizedName) ||
          (hallAdIdsByGroup.has(groupKey) &&
            hallAdIdsByGroup.get(groupKey) ===
              buildHallGroupIdFrom(uploadMeta.current[key]?.hallAdId));
        if (matchesGroup) {
          mutated = true;
          itemsMapRef.current.set(key, { ...entry, pendingCancel: pending });
        }
      });
    }

    if (mutated) {
      syncItemsFromMap();
    }
  };
  class SocketEvents {
    static addOrUpdateUpload(
      id: string,
      name: string,
      feature: feature,
      progressNumber = 0,
      groupId?: string,
    ) {
      // Drop late upserts for ids that already received the conclude event —
      // RTA can redeliver receiveNewUploadItem after concludeUploadItem, which
      // would otherwise resurrect a removed item at 0% forever.
      if (concludedIdsRef.current.has(id)) {
        return;
      }
      if (!permissionsLoadedRef.current) {
        pendingUploadsRef.current = pendingUploadsRef.current.filter((item) => item.id !== id);
        pendingUploadsRef.current.push({
          id,
          name,
          feature,
          progressNumber,
          pendingCancel: false,
          groupId,
        });
        return;
      }

      const permission = UPLOAD_KIND_PERMISSION[feature];
      if (permission && !checkPermission(permissionsRef.current[permission], "view")) return;

      const pendingCancel = ensureUniqueItemKey(id, name, feature, groupId);

      upsertUploadItem(id, {
        id,
        name,
        feature,
        progressNumber,
        pendingCancel,
        groupId,
      });
    }

    static receiveNewUploadItem(id: string, name: string, feature: feature, hallAdId?: string) {
      const groupId = feature === "hall" ? buildHallGroupIdFrom(hallAdId, name) : undefined;
      uploadMeta.current[id] = { name, feature, groupId, hallAdId };
      SocketEvents.addOrUpdateUpload(id, name, feature, 0, groupId);
    }

    static updateUploadItemProgress(
      idOrPayload: any,
      progressNumberArg?: number | string,
      sectionArg?: string,
      metaArg?: any,
    ) {
      const payload =
        typeof idOrPayload === "object" && idOrPayload !== null && "id" in idOrPayload
          ? idOrPayload
          : null;
      // The server emits the POSITIONAL shape (mediaId, progress, section, meta),
      // where the 4th arg carries hallAdId/name. The handler used to declare only
      // three params, so that 4th arg was dropped and every concurrent Atrium
      // upload collapsed into the default "Anúncio do Átrio" group (their groupId
      // fell back to the default name). Capture it so each hall ad keeps its own
      // group regardless of receive-vs-progress ordering.
      const serverMeta =
        !payload && typeof metaArg === "object" && metaArg !== null ? metaArg : null;

      const id = payload ? normalizeUploadId(payload.id) : normalizeUploadId(idOrPayload);
      const progressRaw = payload ? payload.progress : progressNumberArg;
      const section = payload ? payload.section : sectionArg;
      const hallAdId = (payload?.hallAdId ?? serverMeta?.hallAdId) as string | undefined;
      const hallName = (payload?.name ?? serverMeta?.name) as string | undefined;

      const meta = uploadMeta.current[id];
      // Unreachable today: the compressor validates `section` against
      // `oneof=hallAd radioAd tvAd orgAvatar collaboratorAvatar` and all five
      // are mapped above, so nothing else can arrive here. Left as-is rather
      // than guessed at — a new section must be added to the map, not silently
      // absorbed by whatever this default happens to be.
      const sectionFeature =
        section && sectionToFeatureMap[section] ? sectionToFeatureMap[section] : "radio";
      const featureType = meta?.feature ?? sectionFeature;
      const baseName = hallName ?? meta?.name ?? uploadKindDefaultName(featureType);
      const groupId =
        featureType === "hall"
          ? buildHallGroupIdFrom(hallAdId, hallName ?? meta?.name ?? baseName, meta?.groupId)
          : undefined;

      uploadMeta.current[id] = {
        name: baseName,
        feature: featureType,
        groupId,
        hallAdId,
      };
      const normalized =
        typeof progressRaw === "string" ? Number.parseFloat(progressRaw) : progressRaw;
      const safeProgress = Number.isFinite(normalized) ? normalized : 0;

      SocketEvents.addOrUpdateUpload(id, baseName, featureType, safeProgress, groupId);
    }

    static markQueued(id: string, section?: string, notice?: string) {
      const normalizedId = normalizeUploadId(id);
      if (!normalizedId) return;
      // A conclude already landed for this id: do not resurrect it as queued.
      if (concludedIdsRef.current.has(normalizedId)) return;
      // Cosmetic marker only. If permissions have not loaded yet the item (when
      // it exists at all) came through the pending buffer, and a later progress
      // event will render it; skip rather than fight the buffering path.
      if (!permissionsLoadedRef.current) return;

      const meta = uploadMeta.current[normalizedId];
      const sectionFeature =
        section && sectionToFeatureMap[section] ? sectionToFeatureMap[section] : "radio";
      const featureType = meta?.feature ?? sectionFeature;
      const baseName = meta?.name ?? uploadKindDefaultName(featureType);
      const groupId =
        featureType === "hall"
          ? buildHallGroupIdFrom(meta?.hallAdId, baseName, meta?.groupId)
          : undefined;
      uploadMeta.current[normalizedId] = {
        name: baseName,
        feature: featureType,
        groupId,
        hallAdId: meta?.hallAdId,
      };

      const permission = UPLOAD_KIND_PERMISSION[featureType];
      if (permission && !checkPermission(permissionsRef.current[permission], "view")) return;

      upsertQueuedItem(normalizedId, {
        name: baseName,
        feature: featureType,
        groupId,
        notice,
      });

      // A `notice` is the backlog soft reject: the upload was rejected, so no
      // progress or conclude event will ever arrive to clear it. Self-remove
      // after a readable pause, unless a progress event meanwhile flipped it to
      // processing (a real queued job that a worker picked up).
      if (notice) {
        setTimeout(() => {
          const current = itemsMapRef.current.get(normalizedId);
          if (current?.status === "queued") {
            removeUploadItem(normalizedId);
          }
        }, 6000);
      }
    }

    static concludeUploadItem(payload: any) {
      const candidates = [
        normalizeUploadId(payload?.id),
        normalizeUploadId(payload?.mediaId),
        normalizeUploadId((payload as any)?.mediaid),
      ].filter(Boolean);

      const featureFromSectionEarly =
        typeof payload?.section === "string" && sectionToFeatureMap[payload.section]
          ? sectionToFeatureMap[payload.section]
          : undefined;

      if (candidates.length === 0) {
        // No usable id came with the conclude. Fall back to removing the item
        // by section (feature) plus target: for the singleton avatar kinds the
        // feature alone identifies the item; for ad kinds we only match with a
        // name so a concurrent same-kind upload is never wiped. Without this a
        // mis-keyed conclude would leave a ghost stuck at its last percentage.
        if (featureFromSectionEarly) {
          const name = typeof payload?.name === "string" ? payload.name : undefined;
          removeItemsByFeature(featureFromSectionEarly, name);
        }
        return;
      }

      // Avoid repeated handling
      if (candidates.some((id) => concludedIdsRef.current.has(id))) return;
      candidates.forEach((id) => concludedIdsRef.current.add(id));
      // Evict oldest-first (Set preserves insertion order) instead of wiping the
      // whole guard: a total clear() dropped the recent ids too, so a redelivered
      // receiveNewUploadItem for a just-finished upload passed the guard and
      // resurrected the item at 0%. FIFO keeps the newest CONCLUDED_IDS_CAP ids.
      while (concludedIdsRef.current.size > CONCLUDED_IDS_CAP) {
        const oldest = concludedIdsRef.current.values().next().value;
        if (oldest === undefined) break;
        concludedIdsRef.current.delete(oldest);
      }

      const normalizedId = candidates[0];
      const concludedMeta =
        uploadMeta.current[normalizedId] ||
        uploadMeta.current[payload?.id] ||
        uploadMeta.current[payload?.mediaId];
      if (concludedMeta) {
        delete uploadMeta.current[normalizedId];
        delete uploadMeta.current[payload?.id];
        delete uploadMeta.current[payload?.mediaId];
      }

      const featureFromSection =
        typeof payload?.section === "string" && sectionToFeatureMap[payload.section]
          ? sectionToFeatureMap[payload.section]
          : undefined;
      const fallbackInfo =
        concludedMeta && concludedMeta.name
          ? { name: concludedMeta.name, feature: concludedMeta.feature }
          : payload?.name && featureFromSection
            ? { name: payload.name, feature: featureFromSection }
            : undefined;

      const hallAdIdNormalized = normalizeUploadId(payload?.hallAdId);
      const hallGroup =
        (concludedMeta?.feature === "hall" && concludedMeta.groupId) ||
        (hallAdIdNormalized
          ? buildHallGroupId(hallAdIdNormalized)
          : featureFromSection === "hall"
            ? buildHallGroupId(
                normalizeUploadId(payload?.hallAdId) || payload?.name || concludedMeta?.name || "",
              )
            : undefined);

      const remove = () => {
        const idsToRemove = new Set<string>(candidates);
        if (hallGroup) {
          itemsMapRef.current.forEach((entry, key) => {
            if (entry.feature !== "hall") return;
            const metaHallAd = uploadMeta.current[key]?.hallAdId;
            const sameGroup =
              entry.groupId === hallGroup ||
              (metaHallAd && buildHallGroupIdFrom(metaHallAd) === hallGroup) ||
              normalizeNameForKey(entry.name) ===
                normalizeNameForKey(fallbackInfo?.name ?? entry.name);
            if (sameGroup) {
              idsToRemove.add(key);
            }
          });
        }
        idsToRemove.forEach((id) => removeUploadItem(id, fallbackInfo));
      };

      if (String(payload?.status ?? "").toLowerCase() !== "success") {
        remove();
        return;
      }

      setTimeout(() => {
        remove();
        if (featureFromSection) publishAdUploadSettled(featureFromSection, payload?.adId);
      }, 1000);
    }

    // Avatar uploads have no "*-ad.conclude.q" analogue on the wire: enterprise-rta
    // only relays the terminal avatar event as enterpriseRTACollaboratorAvatarStart
    // / enterpriseRTAOrgAvatarUpdate so the profile and org pages can swap the
    // image. The toast never heard a conclude, so the "Perfil" / "Organização" chip
    // sat at ~100% until the 4-minute max-age sweep finally dropped it, long after
    // the media was actually processed. Both avatar kinds are singletons, so the
    // terminal event alone is enough to clear the item by feature. The event fires
    // only on success or failure (both terminal), so clear it either way.
    static concludeCollaboratorAvatar() {
      removeItemsByFeature("collaboratorAvatar");
    }

    static concludeOrgAvatar() {
      removeItemsByFeature("orgAvatar");
    }
  }

  class APIEvents {
    static async fetchActiveUploads() {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) return false;

      const headers = {
        "X-JWT": JWT,
      };

      try {
        const response = await adsAPI.get("/compressions", { headers });
        type ActiveUploadPayload =
          | Array<{
              mediaId?: string;
              targetId?: string;
              name: string;
              feature: feature;
              progress: number;
              status?: string;
            }>
          | {
              activeCompressions?: Array<{
                mediaId?: string;
                targetId?: string;
                name: string;
                feature: feature;
                progress: number;
                status?: string;
              }>;
            };

        const payload = response.data as ActiveUploadPayload;
        const activeUploads = Array.isArray(payload) ? payload : (payload.activeCompressions ?? []);

        const now = Date.now();
        const normalizedMap = new Map(itemsMapRef.current);
        // Keys the server still reports as active. Anything ad-kind in our map
        // that is NOT here (and is past the grace window) is a ghost whose
        // conclude was dropped, and gets pruned below.
        const serverKeys = new Set<string>();
        const currentServerTargets = new Map<string, feature>();
        activeUploads.forEach((upload) => {
          const featureType = upload.feature as feature;
          const normalizedMediaId = normalizeUploadId(upload.mediaId);
          const normalizedTargetId = normalizeUploadId(upload.targetId);
          if (normalizedTargetId) currentServerTargets.set(normalizedTargetId, featureType);
          const fallbackKey = buildFallbackKey(upload.name, featureType);
          const nameMatchKey = findKeyByNameFeature(upload.name, featureType);
          const desiredKey = normalizedMediaId || normalizedTargetId || fallbackKey;
          serverKeys.add(desiredKey);
          if (nameMatchKey) serverKeys.add(nameMatchKey);
          const existing =
            (normalizedMediaId ? normalizedMap.get(normalizedMediaId) : undefined) ??
            (nameMatchKey ? normalizedMap.get(nameMatchKey) : undefined) ??
            normalizedMap.get(fallbackKey);

          if (normalizedMediaId && nameMatchKey && nameMatchKey !== normalizedMediaId) {
            normalizedMap.delete(nameMatchKey);
          }

          const payload: UploadToastItems = {
            id: desiredKey,
            name: upload.name,
            feature: featureType,
            progressNumber: upload.progress,
            pendingCancel: existing?.pendingCancel ?? false,
            groupId:
              featureType === "hall"
                ? buildHallGroupId((upload as any).hallAdId ?? upload.name)
                : undefined,
            status:
              upload.status === "waiting"
                ? "queued"
                : upload.status === "finalizing"
                  ? "finalizing"
                  : undefined,
            startedAt: existing?.startedAt ?? now,
            updatedAt: now,
          };

          normalizedMap.set(desiredKey, payload);
          if (featureType === "hall") {
            const hallAdId = normalizeUploadId((upload as any).hallAdId);
            uploadMeta.current[desiredKey] = {
              name: upload.name,
              feature: featureType,
              groupId: payload.groupId,
              hallAdId,
            };
          }
        });

        serverPendingTargetsRef.current.forEach((featureType, targetId) => {
          if (!currentServerTargets.has(targetId)) {
            publishAdUploadSettled(featureType, targetId);
          }
        });
        serverPendingTargetsRef.current = currentServerTargets;

        // Prune ad-kind ghosts: items GET /compressions no longer reports as
        // active, that have also been quiet past the grace window. Avatar kinds
        // are intentionally skipped (that endpoint does not report them, so
        // their absence proves nothing); the max-age sweep covers those.
        for (const [key, entry] of Array.from(normalizedMap.entries())) {
          if (!AD_FEATURES.has(entry.feature)) continue;
          if (serverKeys.has(key)) continue;
          if (entry.pendingCancel) continue;
          const quietFor = now - (entry.updatedAt ?? entry.startedAt ?? now);
          if (quietFor >= GHOST_GRACE_MS) {
            normalizedMap.delete(key);
          }
        }

        itemsMapRef.current = normalizedMap;
        syncItemsFromMap();
      } catch (error) {
        console.error("Error fetching active uploads:", error);
      }
    }

    static async cancelUpload(id: string | string[]) {
      const JWT = (await localforage.getItem("JWT")) as string | undefined;
      if (!JWT) return false;

      const headers = {
        "X-JWT": JWT,
      };

      const ids = Array.isArray(id) ? id : [id];
      const idsToCancel = new Set<string>();
      ids.forEach((rawId) => {
        const normalized = normalizeUploadId(rawId);
        if (!normalized) return;
        idsToCancel.add(normalized);
        const entry = itemsMapRef.current.get(normalized);
        const meta = uploadMeta.current[normalized];
        if (entry?.feature === "hall" || meta?.feature === "hall") {
          const groupKey =
            entry?.groupId ??
            buildHallGroupIdFrom(meta?.hallAdId, entry?.name ?? meta?.name, meta?.groupId);
          const normalizedName = normalizeNameForKey(entry?.name ?? meta?.name ?? "");
          itemsMapRef.current.forEach((item, key) => {
            if (item.feature !== "hall") return;
            const itemMeta = uploadMeta.current[key];
            const sameGroup =
              (groupKey && item.groupId === groupKey) ||
              (groupKey &&
                itemMeta?.hallAdId &&
                buildHallGroupIdFrom(itemMeta.hallAdId) === groupKey) ||
              (normalizedName && normalizeNameForKey(item.name) === normalizedName);
            if (sameGroup) {
              idsToCancel.add(key);
            }
          });
        }
      });

      try {
        for (const mediaId of idsToCancel) {
          const body = { mediaId };
          await compressorAPI.post(`/cancel`, body, { headers });
        }
        updatePendingCancel(Array.from(idsToCancel), true);
        return true;
      } catch (error) {
        console.error("Error cancelling upload:", error);
        updatePendingCancel(Array.from(idsToCancel), false);
        return false;
      }
    }
  }

  React.useEffect(() => {
    RTA.connect();
    APIEvents.fetchActiveUploads();
  }, []);

  // Keep the list honest even when a conclude event never lands. fetchActiveUploads
  // ran only once on mount, so a dropped or mis-keyed conclude left a ghost stuck
  // forever. Reconcile on a light interval and whenever the tab regains focus, and
  // sweep any item that has gone quiet past the hard ceiling. Together these make
  // the "Uploads em Andamento" flow self-heal under concurrent multi-collaborator
  // uploads.
  React.useEffect(() => {
    const reconcile = () => {
      APIEvents.fetchActiveUploads();
    };
    const sweepStaleItems = () => {
      const now = Date.now();
      let mutated = false;
      for (const [key, entry] of Array.from(itemsMapRef.current.entries())) {
        if (entry.pendingCancel) continue;
        const quietFor = now - (entry.updatedAt ?? entry.startedAt ?? now);
        if (quietFor >= ITEM_MAX_AGE_MS) {
          itemsMapRef.current.delete(key);
          mutated = true;
        }
      }
      if (mutated) syncItemsFromMap();
    };

    const intervalId = window.setInterval(() => {
      sweepStaleItems();
      reconcile();
    }, RECONCILE_INTERVAL_MS);

    const onFocus = () => {
      if (document.visibilityState === "hidden") return;
      sweepStaleItems();
      reconcile();
    };
    window.addEventListener("focus", onFocus);
    document.addEventListener("visibilitychange", onFocus);

    return () => {
      window.clearInterval(intervalId);
      window.removeEventListener("focus", onFocus);
      document.removeEventListener("visibilitychange", onFocus);
    };
  }, []);

  React.useEffect(() => {
    permissionsRef.current = permissions;
  }, [permissions]);

  React.useEffect(() => {
    permissionsLoadedRef.current = permissionsLoaded;
    if (!permissionsLoaded) {
      return;
    }
    if (pendingUploadsRef.current.length === 0) {
      return;
    }

    const queuedItems = [...pendingUploadsRef.current];
    pendingUploadsRef.current = [];
    queuedItems.forEach((upload) => {
      SocketEvents.addOrUpdateUpload(upload.id, upload.name, upload.feature, upload.progressNumber);
    });
  }, [permissionsLoaded]);

  React.useEffect(() => {
    onNewUploadRef.current = props.onNewUpload;
  }, [props.onNewUpload]);

  React.useEffect(() => {
    if (!props.isOpen && items.length > 0 && prevItemsLengthRef.current === 0) {
      onNewUploadRef.current?.();
    }
    prevItemsLengthRef.current = items.length;
  }, [items.length, props.isOpen]);

  React.useEffect(() => {
    let cleanup = () => {};
    let isActive = true;
    sessionInfo()
      .then((info) => {
        if (!isActive || !info) return;
        const receiveEvent = `receiveNewUploadItem-${info.org_id}`;
        const progressEvent = `updateUploadItemProgress-${info.org_id}`;
        const concludeEvent = `concludeUploadItem-${info.org_id}`;
        // Avatar conclude channels. The toast is mounted app-wide, so it clears
        // the avatar chip even when the user has already navigated away from
        // /configuracoes or /organizacao. Same event names the profile and org
        // pages subscribe to: the collaborator avatar is per-uploader, the org
        // logo is company-wide (no uploader suffix).
        const collaboratorAvatarEvent = `enterpriseRTACollaboratorAvatarStart-${info.org_id}-${info.iss}`;
        const orgAvatarEvent = `enterpriseRTAOrgAvatarUpdate-${info.org_id}`;

        RTA.on(receiveEvent, SocketEvents.receiveNewUploadItem);
        RTA.on(progressEvent, SocketEvents.updateUploadItemProgress);
        RTA.on(concludeEvent, SocketEvents.concludeUploadItem);
        RTA.on(collaboratorAvatarEvent, SocketEvents.concludeCollaboratorAvatar);
        RTA.on(orgAvatarEvent, SocketEvents.concludeOrgAvatar);

        cleanup = () => {
          RTA.off(receiveEvent, SocketEvents.receiveNewUploadItem);
          RTA.off(progressEvent, SocketEvents.updateUploadItemProgress);
          RTA.off(concludeEvent, SocketEvents.concludeUploadItem);
          RTA.off(collaboratorAvatarEvent, SocketEvents.concludeCollaboratorAvatar);
          RTA.off(orgAvatarEvent, SocketEvents.concludeOrgAvatar);
        };
      })
      .catch((err) => {
        console.error("Error retrieving session info:", err);
      });
    return () => {
      isActive = false;
      cleanup();
    };
  }, []);

  React.useEffect(() => {
    registerLocalUploadProgressEmitter((id, progress, section) =>
      SocketEvents.updateUploadItemProgress(id, progress, section),
    );
    return () => registerLocalUploadProgressEmitter(null);
  }, []);

  React.useEffect(() => {
    registerUploadQueuedEmitter((id, section, notice) =>
      SocketEvents.markQueued(id, section, notice),
    );
    return () => registerUploadQueuedEmitter(null);
  }, []);

  // Wire the module-level opener so pages can pop the drawer open imperatively
  // (e.g. ads.tsx calls triggerUploadToastOpen() the moment an upload starts).
  // Without this registration that trigger was a silent no-op. Route it through
  // the same onNewUpload callback Root uses to flip the drawer open.
  React.useEffect(() => {
    registerUploadToastOpener(() => onNewUploadRef.current?.());
    return () => registerUploadToastOpener(null);
  }, []);

  const viewItems = React.useMemo(() => {
    const hallGroups = new Map<
      string,
      {
        name: string;
        total: number;
        count: number;
        pending: boolean;
        members: string[];
        queuedCount: number;
        notice?: string;
      }
    >();
    const others: UploadToastItems[] = [];

    items.forEach((item) => {
      if (item.feature !== "hall") {
        others.push(item);
        return;
      }
      const groupKey = item.groupId ?? buildHallGroupId(item.name);
      const existing = hallGroups.get(groupKey) ?? {
        name: item.name,
        total: 0,
        count: 0,
        pending: false,
        members: [],
        queuedCount: 0,
      };
      existing.total += item.progressNumber;
      existing.count += 1;
      existing.pending = existing.pending || !!item.pendingCancel;
      if (item.status === "queued") {
        existing.queuedCount += 1;
        existing.notice = existing.notice ?? item.notice;
      }
      existing.members.push(item.id);
      hallGroups.set(groupKey, existing);
    });

    const hallAggregated: UploadToastItems[] = Array.from(hallGroups.entries()).map(
      ([groupKey, group]) => {
        const avg = group.count > 0 ? group.total / group.count : 0;
        const progressNumber = Math.min(100, Math.max(0, Number(avg.toFixed(2))));
        // The group reads as queued only while every member is still queued; the
        // first member a worker picks up flips the whole group to processing.
        const allQueued = group.count > 0 && group.queuedCount === group.count;
        return {
          id: groupKey,
          name: group.name,
          feature: "hall",
          progressNumber,
          pendingCancel: group.pending,
          groupId: groupKey,
          memberIds: group.members,
          status: allQueued ? ("queued" as const) : undefined,
          notice: allQueued ? group.notice : undefined,
        };
      },
    );

    return [...hallAggregated, ...others];
  }, [items]);

  // Auto-cleanup hall groups when all members reached 100% and are not pending cancel.
  React.useEffect(
    () => () => {
      concludedIdsRef.current.clear();
    },
    [],
  );

  if (!props.isOpen) return <></>;
  return (
    <div id="uploadToastContainer">
      <div id="uploadToastTitle">
        <span>Uploads em Andamento</span>
        <button onClick={props.onClose}>
          <CloseRounded />
        </button>
      </div>

      {viewItems.length === 0 ? (
        <NoUploadsInProgress />
      ) : (
        <div id="uploadToastItemsContainer">
          {viewItems.map((item, index) => (
            <UploadToastItem
              key={item.id}
              item={item}
              isLast={index === viewItems.length - 1}
              onCancelClick={(idOrIds) => APIEvents.cancelUpload(idOrIds)}
            />
          ))}
        </div>
      )}
    </div>
  );
};
